/**
 * Spatial Canvas Component - Main orchestrator
 * Brings together all spatial canvas components for the unified view
 */

import React, { Suspense, useState, Component, useRef, useEffect } from 'react';
import type { UserId } from '@maco144/shared';
import { CANVAS_THEME } from './canvas-theme';
import { GridOverlay } from './grid-overlay';
import { CanvasToolbar } from './canvas-toolbar';
import { DraggablePanel } from './draggable-panel';
import { panelToScreen, screenToPanel } from './grid-utils';
import { useCanvasState } from './use-canvas-state';
import { useIdentity } from './use-identity';
import { useBackend } from './use-backend';
import {
  useGlobalPreferences,
  type GlobalPreferencesContextType,
} from './use-global-preferences';
import { getPanelEntry } from './panel-registry';
import type { PanelType } from './panel-layout-store';
import { autoLayoutPanels } from './default-layout';
import { CommandPalette } from './command-palette';
import { KeyboardShortcutsOverlay } from './keyboard-shortcuts-overlay';
import { Minimap } from './minimap';
import { useWSEventBridge } from './use-ws-event-bridge';
import { OPEN_PANEL_EVENT } from './onboarding-tour';
import './spatial-canvas.css';
import './minimap.css';

function trackRecentPanel(type: PanelType) {
  const key = 'concord-recent-panels';
  const existing: PanelType[] = JSON.parse(localStorage.getItem(key) ?? '[]');
  const updated = [type, ...existing.filter((t) => t !== type)].slice(0, 5);
  localStorage.setItem(key, JSON.stringify(updated));
}

/**
 * Safe wrapper for useGlobalPreferences
 * Returns null if PreferencesProvider is not available (graceful degradation)
 */
function useSafeGlobalPreferences(): GlobalPreferencesContextType | null {
  try {
    return useGlobalPreferences();
  } catch {
    return null;
  }
}

export interface SpatialCanvasProps {
  userId: UserId;
  onPanelClose?: (panelType: PanelType) => void;
  onZoomChange?: (zoomLevel: number, gridSize: number) => void;
}

/**
 * Error Boundary for lazy-loaded panels
 */
class PanelErrorBoundary extends Component<
  { children: React.ReactNode; panelType: PanelType },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; panelType: PanelType }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error(`[PanelErrorBoundary] Failed to load panel ${this.props.panelType}:`, error?.message, error?.stack);
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const msg = err?.message || String(err) || 'Unknown error';
      const stack = err?.stack?.split('\n').slice(0, 4).join('\n') || '';
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            color: '#ef4444',
            fontSize: '12px',
            padding: '16px',
            overflowY: 'auto',
            fontFamily: 'monospace',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>⚠️ {this.props.panelType} error</div>
          <div style={{ color: '#fca5a5', marginBottom: '8px', wordBreak: 'break-all' }}>{msg}</div>
          {stack && <pre style={{ color: '#666', fontSize: '10px', whiteSpace: 'pre-wrap', margin: 0 }}>{stack}</pre>}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Panel Loader with Fallback
 * Handles lazy-loaded panel components
 */
function PanelContent({
  panelType,
  _panelId,
  onClose,
  userId,
  extraProps,
}: {
  panelType: PanelType;
  panelId: string;
  onClose: () => void;
  userId?: string;
  extraProps?: Record<string, unknown>;
}) {
  const entry = getPanelEntry(panelType);

  return (
    <PanelErrorBoundary panelType={panelType}>
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: CANVAS_THEME.textSecondary,
              fontSize: '14px',
            }}
          >
            Loading {entry.title}...
          </div>
        }
      >
        <entry.Component onClose={onClose} userId={userId} {...(extraProps ?? {})} />
      </Suspense>
    </PanelErrorBoundary>
  );
}

/**
 * Spatial Canvas - Main component
 * Complete spatial canvas interface with draggable/resizable panels
 */
export function SpatialCanvas({ userId, onPanelClose, onZoomChange }: SpatialCanvasProps) {
  const { currentIdentity } = useIdentity();
  const { pendingInvites, client } = useBackend();
  // Event bridge uses the same client — no second BrowserClient instance
  useWSEventBridge(client);
  const globalPreferences = useSafeGlobalPreferences();
  const canvasState = useCanvasState(userId, globalPreferences);
  const [showGrid, setShowGrid] = useState(true);
  const [showPalette, setShowPalette] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  // mousePos is only needed in the wheel handler which reads e.clientX directly —
  // using state here caused a full re-render of all panels on every mousemove.
  const mousePosRef = useRef({ x: 0, y: 0 });
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // Min/max grid sizes (8px to 200px)
  const MIN_GRID_SIZE = 8;
  const MAX_GRID_SIZE = 200;
  const PAN_THRESHOLD_ZOOM = 1.5; // Enable panning at 150% zoom (grid > 30px)

  /**
   * Track mouse position for zoom focus (ref — no re-renders)
   */
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  /**
   * Open command palette on Cmd+K / Ctrl+K
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowPalette((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  /**
   * Single-key shortcuts and `?` help overlay
   * Only fires when no input/textarea is focused (activeElement is body or null)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't fire when modifier keys are held (except Shift for `?`)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const active = document.activeElement;
      const inputFocused =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);

      if (e.key === '?') {
        if (!inputFocused) {
          e.preventDefault();
          setShowShortcuts((prev) => !prev);
        }
        return;
      }

      if (e.key === 'Escape') {
        setShowShortcuts(false);
        return;
      }

      // Single-key panel shortcuts — only when nothing is focused
      const noInputFocused = !inputFocused && (active === document.body || active === null);
      if (!noInputFocused) return;

      switch (e.key) {
        case 't':
          canvasState.addPanel('topics');
          trackRecentPanel('topics');
          break;
        case 'n':
          canvasState.addPanel('notes');
          trackRecentPanel('notes');
          break;
        case 'c':
          canvasState.addPanel('private-chat');
          trackRecentPanel('private-chat');
          break;
        case 'a':
          canvasState.addPanel('avid-channels');
          trackRecentPanel('avid-channels');
          break;
        case 'f':
          canvasState.addPanel('groups');
          trackRecentPanel('groups');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvasState]);

  /**
   * Open panels on demand from the onboarding tour.
   */
  useEffect(() => {
    const handleOpenPanel = (e: Event) => {
      const type = (e as CustomEvent<PanelType>).detail;
      if (!type) return;
      canvasState.addPanel(type);
      trackRecentPanel(type);
    };
    window.addEventListener(OPEN_PANEL_EVENT, handleOpenPanel);
    return () => window.removeEventListener(OPEN_PANEL_EVENT, handleOpenPanel);
  }, [canvasState]);

  /**
   * Attach wheel event listener to canvas
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleCanvasWheel = (e: WheelEvent) => {
      // Only zoom if scrolling over the background (not over a panel)
      const target = e.target as HTMLElement;
      const isOnBackground =
        target.classList.contains('spatial-canvas') ||
        target === canvas ||
        target.classList.contains('grid-overlay');

      if (isOnBackground) {
        e.preventDefault();

        // Get cursor position for zoom focus
        const cursorX = e.clientX;
        const cursorY = e.clientY;

        // Scroll up = zoom in (smaller grid), scroll down = zoom out (larger grid)
        const zoomDelta = e.deltaY > 0 ? 1.1 : 0.909; // ~10% per scroll
        const newGridSize = Math.max(
          MIN_GRID_SIZE,
          Math.min(MAX_GRID_SIZE, canvasState.gridSize * zoomDelta)
        );

        if (Math.abs(newGridSize - canvasState.gridSize) > 0.5) {
          const roundedSize = Math.round(newGridSize);

          // Calculate zoom focus to keep cursor point stable
          // Old and new zoom scales
          const oldZoomScale = 20 / canvasState.gridSize;
          const newZoomScale = 20 / roundedSize;

          // Calculate what world coordinate the cursor is pointing to (relative to pan)
          const worldPointX = (cursorX - panOffset.x) / oldZoomScale;
          const worldPointY = (cursorY - panOffset.y) / oldZoomScale;

          // Calculate new pan offset so the same world point stays under the cursor
          const newPanX = cursorX - worldPointX * newZoomScale;
          const newPanY = cursorY - worldPointY * newZoomScale;

          // Apply new pan offset to keep cursor focused
          setPanOffset({ x: newPanX, y: newPanY });

          canvasState.setGridSize(roundedSize);
          const newZoomLevel = roundedSize / 20; // Reference 20px as 1x zoom
          setZoomLevel(newZoomLevel);

          // Notify parent of zoom change
          onZoomChange?.(newZoomLevel, roundedSize);

          // Log zoom level (dev debug)
          const zoomPercent = Math.round(newZoomLevel * 100);
          // console.log(`📏 Zoom: ${zoomPercent}% (grid: ${roundedSize}px) [focused at cursor]`);
        }
      }
    };

    canvas.addEventListener('wheel', handleCanvasWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', handleCanvasWheel);
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, [canvasState, panOffset, onZoomChange]);

  /**
   * Pan/drag canvas when zoomed out past 150%
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || zoomLevel <= PAN_THRESHOLD_ZOOM) return;

    const handlePanMouseDown = (e: MouseEvent) => {
      // Only pan if clicking on background (not on panels or toolbar)
      const target = e.target as HTMLElement;
      const isOnBackground =
        target.classList.contains('spatial-canvas') ||
        target === canvas ||
        target.classList.contains('grid-overlay');

      if (isOnBackground && e.button === 0) {
        // Left mouse button only
        setIsPanning(true);
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          offsetX: panOffset.x,
          offsetY: panOffset.y,
        };
        canvas.style.cursor = 'grabbing';
      }
    };

    const handlePanMouseMove = (e: MouseEvent) => {
      if (!isPanning) return;

      const deltaX = e.clientX - panStartRef.current.x;
      const deltaY = e.clientY - panStartRef.current.y;

      setPanOffset({
        x: panStartRef.current.offsetX + deltaX,
        y: panStartRef.current.offsetY + deltaY,
      });
    };

    const handlePanMouseUp = () => {
      setIsPanning(false);
      canvas.style.cursor = zoomLevel > PAN_THRESHOLD_ZOOM ? 'grab' : 'default';
    };

    // Update cursor based on zoom level
    const updateCursor = () => {
      if (canvas) {
        canvas.style.cursor = zoomLevel > PAN_THRESHOLD_ZOOM ? 'grab' : 'default';
      }
    };

    document.addEventListener('mousemove', handlePanMouseMove);
    document.addEventListener('mouseup', handlePanMouseUp);
    canvas.addEventListener('mousedown', handlePanMouseDown);
    updateCursor();

    return () => {
      document.removeEventListener('mousemove', handlePanMouseMove);
      document.removeEventListener('mouseup', handlePanMouseUp);
      canvas.removeEventListener('mousedown', handlePanMouseDown);
    };
  }, [zoomLevel, isPanning, panOffset]);

  if (canvasState.isLoading) {
    return (
      <div
        className="spatial-canvas"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: 'white', fontSize: '18px' }}>🌅 Loading spatial canvas...</div>
      </div>
    );
  }

  const userIdentityStr = currentIdentity
    ? `${currentIdentity.phrase.adjective} ${currentIdentity.phrase.noun}`
    : undefined;

  return (
    <div ref={canvasRef} className="spatial-canvas">
      {/* Grid Overlay */}
      {showGrid && <GridOverlay gridSize={canvasState.gridSize} />}

      {/* Zoom Indicator */}
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          zIndex: CANVAS_THEME.zIndex.toolbar - 1,
          pointerEvents: 'none',
          opacity: zoomLevel !== 1 ? 1 : 0,
          transition: 'opacity 0.3s ease',
          fontFamily: 'monospace',
        }}
      >
        📏 {Math.round(zoomLevel * 100)}%
      </div>

      {/* Toolbar */}
      <CanvasToolbar
        onAddPanel={(type) => {
          canvasState.addPanel(type);
          trackRecentPanel(type);
        }}
        onResetLayout={canvasState.resetLayout}
        onToggleGrid={() => setShowGrid(!showGrid)}
        showGrid={showGrid}
        userIdentity={userIdentityStr}
        unreadCount={pendingInvites.length}
        onOpenSettings={() => {
          canvasState.addPanel('settings');
          trackRecentPanel('settings');
        }}
      />

      {/* Command Palette */}
      {showPalette && (
        <CommandPalette
          onOpenPanel={(type) => {
            canvasState.addPanel(type);
            trackRecentPanel(type);
            setShowPalette(false);
          }}
          onClose={() => setShowPalette(false)}
        />
      )}

      {/* Keyboard Shortcuts Overlay */}
      {showShortcuts && (
        <KeyboardShortcutsOverlay onClose={() => setShowShortcuts(false)} />
      )}

      {/* Panels - Scaled by zoom level */}
      <Minimap
        panels={canvasState.panels}
        viewportWidth={window.innerWidth}
        viewportHeight={window.innerHeight}
        onPanelClick={(panelId) => canvasState.bringToFront(panelId)}
      />

      {Array.from(canvasState.panels.values()).map((panel) => {
        const entry = getPanelEntry(panel.type);

        // Scale panel position and size based on zoom level (reference: 20px = 1x)
        // Lower zoom (e.g., 0.4x/40%) = panels appear larger (more detail)
        // Higher zoom (e.g., 2x/200%) = panels appear smaller (overview)
        const zoomScale = 20 / canvasState.gridSize; // Inverse scaling
        // Pan only applies past the threshold. Both directions must use the
        // same offset, or dragging re-adds it and the panel teleports.
        const activePanOffset = zoomLevel > PAN_THRESHOLD_ZOOM ? panOffset : { x: 0, y: 0 };
        const scaledPosition = panelToScreen(panel.position, zoomScale, activePanOffset);
        const scaledSize = {
          width: panel.size.width * zoomScale,
          height: panel.size.height * zoomScale,
        };

        return (
          <DraggablePanel
            key={panel.id}
            id={panel.id}
            type={panel.type}
            position={scaledPosition}
            size={scaledSize}
            zIndex={panel.zIndex}
            minimized={panel.minimized}
            gridSize={canvasState.gridSize}
            title={entry.title}
            badgeCount={panel.type === 'private-chat' ? pendingInvites.length : undefined}
            onMove={(id, newPos) => {
              // Back to canvas space — removes the pan offset, then unscales
              canvasState.movePanel(id, screenToPanel(newPos, zoomScale, activePanOffset));
            }}
            onResize={(id, newSize) => {
              // Unscale size before saving
              const unscaledSize = {
                width: newSize.width / zoomScale,
                height: newSize.height / zoomScale,
              };
              canvasState.resizePanel(id, unscaledSize);
            }}
            onClose={(id) => {
              canvasState.closePanel(id);
              onPanelClose?.(panel.type);
            }}
            onMinimize={canvasState.minimizePanel}
            onBringToFront={canvasState.bringToFront}
          >
            <PanelContent
              panelType={panel.type}
              panelId={panel.id}
              onClose={() => canvasState.closePanel(panel.id)}
              userId={userId}
              extraProps={
                panel.type === 'settings'
                  ? {
                      onAutoLayout: () =>
                        canvasState.setPanels(autoLayoutPanels(canvasState.panels)),
                    }
                  : undefined
              }
            />
          </DraggablePanel>
        );
      })}
    </div>
  );
}
